import cn from 'classnames';
import { Fragment, useMemo } from 'react';
import { ROW_SPACER_HEIGHT, TIMEZONE_COLUMN_WIDTH } from '../constants';
import type { CalendarEvent, CalendarMode, EventWithDimensions, RenderEvent, Timezones } from '../types';
import { EventItem } from './event-item';
import styles from './events.module.css';
import { useCalculateDayEvents, useGetEventStyle } from './hooks';

type Properties<T extends CalendarEvent> = {
  days: Date[];
  events: T[];
  mode: CalendarMode;
  renderEvent?: RenderEvent<T>;
  timezones: Timezones;
};

const Events = <T extends CalendarEvent>({ days, events, mode, renderEvent, timezones }: Properties<T>) => {
  const calculateDayEvents = useCalculateDayEvents();
  const getEventStyle = useGetEventStyle();

  const eventsByDay = useMemo(() => {
    const eventsMap = new Map<string, Array<EventWithDimensions<T>>>();

    for (const day of days) {
      const dayEvents = calculateDayEvents({ day, events });

      if (dayEvents.length > 0) {
        eventsMap.set(day.toISOString(), dayEvents);
      }
    }

    return eventsMap;
  }, [days, events, calculateDayEvents]);

  return (
    <div
      className={cn(styles['events-container'], {
        [styles['week-grid'] ?? '']: mode === 'week',
      })}
      style={{
        left: `${TIMEZONE_COLUMN_WIDTH * timezones.length}px`,
        top: `${ROW_SPACER_HEIGHT}px`,
      }}
    >
      {days.map((day) => {
        const dayEvents = eventsByDay.get(day.toISOString()) ?? [];

        return (
          <div className={styles['events-day']} key={day.toISOString()}>
            {dayEvents.map((event) => (
              <Fragment key={event.id}>
                {renderEvent ? (
                  renderEvent({
                    children: (
                      <div style={getEventStyle({ event })}>
                        <EventItem<T>
                          calendarEvent={event}
                          style={getEventStyle({ event, includePositioning: false })}
                        />
                      </div>
                    ),
                    event,
                  })
                ) : (
                  <EventItem<T> calendarEvent={event} style={getEventStyle({ event })} />
                )}
              </Fragment>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default Events;
